import { lazy, Suspense, useEffect, useState } from 'react'
import '@excalidraw/excalidraw/index.css'
import type { DiagramScene } from './scene'

/**
 * The real Excalidraw, in the product.
 *
 * LAZY, AND ONLY THIS FILE IMPORTS IT. The browser bundle is well over a
 * megabyte with its fonts, and most conversations never ask for a
 * diagram. Importing it anywhere that loads with the dock would put that
 * on every page of Connect for the sake of a feature nobody has asked
 * for yet, so the whole component sits behind a dynamic import and the
 * panel renders a line of text until it arrives.
 *
 * IT IS THE EDITOR, not a picture of one. Once a diagram is on screen it
 * can be dragged, relabelled, drawn on and exported, because the thing
 * that makes Excalidraw worth the weight is that the output is yours to
 * change rather than a render to accept or reject.
 */

const Excalidraw = lazy(async () => {
  const mod = await import('@excalidraw/excalidraw')
  return { default: mod.Excalidraw }
})

export function ExcalidrawCanvas({
  scene,
  onApi,
}: {
  scene: DiagramScene | null
  /** The imperative handle, so a later turn can update the scene in place. */
  onApi?: (api: ExcalidrawHandle | null) => void
}) {
  return (
    <Suspense
      fallback={
        <p className="p-4 text-sm text-muted-foreground">Opening the canvas…</p>
      }
    >
      <Canvas scene={scene} onApi={onApi} />
    </Suspense>
  )
}

/** The bits of Excalidraw's API this uses. Named so the lazy import stays typed. */
export interface ExcalidrawHandle {
  updateScene: (scene: { elements: readonly unknown[] }) => void
  scrollToContent: (
    target?: unknown,
    opts?: { fitToContent?: boolean; animate?: boolean },
  ) => void
}

function Canvas({
  scene,
  onApi,
}: {
  scene: DiagramScene | null
  onApi?: (api: ExcalidrawHandle | null) => void
}) {
  const [api, setApi] = useState<ExcalidrawHandle | null>(null)

  /**
   * A NEW SCENE REPLACES, it does not append.
   *
   * The agent redrawing after "no, put the deposit step before the
   * confirmation" means a new diagram, not a second one beside the first.
   * `scrollToContent` with `fitToContent` then frames it, because an
   * updated scene keeps the old viewport and a diagram that grew is
   * otherwise half off the edge.
   */
  useEffect(() => {
    if (!api || !scene) return
    api.updateScene({ elements: scene.elements })
    api.scrollToContent(undefined, { fitToContent: true, animate: true })
  }, [api, scene])

  return (
    <Excalidraw
      excalidrawAPI={(instance: unknown) => {
        const handle = instance as ExcalidrawHandle
        setApi(handle)
        onApi?.(handle)
      }}
      initialData={{
        elements: (scene?.elements ?? []) as never,
        appState: {
          viewBackgroundColor: 'transparent',
          // The agent's diagrams are flat by design, and a grid behind a
          // flat diagram on frosted glass is three competing textures.
          gridSize: null,
        },
        scrollToContent: true,
      }}
      UIOptions={{
        canvasActions: {
          // No "load from file" and no live collaboration: this canvas
          // belongs to a conversation, and both of those are doors out of
          // it into somebody else's drawing.
          loadScene: false,
          saveToActiveFile: false,
          toggleTheme: false,
        },
      }}
    />
  )
}
